import EditCourseMaterial from "@/components/EditCourseMaterial/EditCourseMaterial";
import MaterialRow from "@/components/MaterialRow/MaterialRow";
import { ViewChapter } from "@/components/ViewChapter/ViewChapter";
import { ViewUnit } from "@/components/ViewUnit/ViewUnit";
import { AUTHORITIES } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoChapter } from "@/shared/model/edo-chapter.model";
import { IEdoCourseMaterial } from "@/shared/model/edo-course-material.model";
import { IEdoUnit } from "@/shared/model/edo-unit.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getCourseMaterialsByCourse } from "@/shared/reducers/entities/edo-course-material.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { ITEMS_PER_PAGE } from "@/shared/util/pagination.constants";
import { Card, IconButton, Pagination } from "@/components/ui";
import { Button } from "@/components/ui";
import { Loader2, Plus, RefreshCw } from "lucide-react";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { IPaginationBaseState, Translate } from "react-jhipster";
import { useParams } from "react-router-dom";
import DeleteCourseMaterial from "../../components/DeleteCourseMaterial";

const CourseMaterials = () => {
  const params = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const {
    loading: loadingMaterialeInsegnamento,
    totalItems: materialeInsegnamentoTotalItems,
    entities: courseMaterials,
    updateSuccess: updateSuccessCourseMaterial,
  } = useAppSelector<EntityState<IEdoCourseMaterial>>(
    (state) => state.edoCourseMaterial
  );
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [materialeToDelete, setMaterialeToDelete] =
    useState<IEdoCourseMaterial>(null);
  const [viewUnitDialog, setViewUnitDialog] = useState<boolean>(false);
  const [unitToView, setUnitToView] = useState<IEdoUnit>(null);
  const [viewChapterDialog, setViewChapterDialog] = useState<boolean>(false);
  const [chapterToView, setChapterToView] = useState<IEdoChapter>(null);
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "order",
    order: "ASC",
  });
  const [courseMaterialsLocal, setCourseMaterialsLocal] = useState<
    IEdoCourseMaterial[]
  >(Array.from(courseMaterials));
  const [courseMaterialToEdit, setCourseMaterialToEdit] =
    useState<IEdoCourseMaterial>(null);
  const [editCourseMaterialDialog, setEditCourseMaterialDialog] =
    useState<boolean>(false);

  const getAllEntities = () => {
    dispatch(
      getCourseMaterialsByCourse({
        courseId: params.id,
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  useEffect(() => {
    getAllEntities();
  }, [
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  const handlePagination = (currentPage: number) =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  const handleSyncList = () => {
    getAllEntities();
  };

  const handleOnDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(courseMaterialsLocal);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setCourseMaterialsLocal(Array.from(items));
    try {
      await axios.patch(
        `/course-materials/${result.draggableId}/order`,
        { order: result.destination.index + 1 }
      );
    } catch (e) {
      getAllEntities();
    }
  };

  const handleOpenEditCourseMaterial = (courseMaterial: IEdoCourseMaterial) => {
    setCourseMaterialToEdit(courseMaterial);
    setEditCourseMaterialDialog(true);
  };

  const handleCloseEditCourseMaterialDialog = () => {
    setEditCourseMaterialDialog(false);
    setCourseMaterialToEdit(null);
  };

  const handleOpenDeleteDialog = (materiale: IEdoCourseMaterial) => {
    setMaterialeToDelete(materiale);
    setOpenDeleteDialog(true);
  };
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setMaterialeToDelete(null);
  };

  const handleOpenViewUnitDialog = (unit: IEdoUnit) => {
    setUnitToView(unit);
    setViewUnitDialog(true);
  };

  const handleCloseViewUnitDialog = () => {
    setViewUnitDialog(false);
    setUnitToView(null);
  };

  const handleOpenViewChapter = (chapter: IEdoChapter) => {
    setChapterToView(chapter);
    setViewChapterDialog(true);
  };

  const handleCloseViewChapter = () => {
    setViewChapterDialog(false);
    setChapterToView(null);
  };

  const isAdmin = useMemo(
    () =>
      hasAnyAuthority(account.roles, [
        AUTHORITIES.ROLE_ADMIN,
        AUTHORITIES.ROLE_VICE,
        AUTHORITIES.ROLE_TEACHER,
      ]),
    [account.roles]
  );

  useEffect(() => {
    setCourseMaterialsLocal(Array.from(courseMaterials));
  }, [courseMaterials]);

  useEffect(() => {
    if (updateSuccessCourseMaterial) {
      getAllEntities();
    }
  }, [updateSuccessCourseMaterial]);

  const handleOnDeleteCourseMaterial = (courseMaterial: IEdoCourseMaterial) => {
    setCourseMaterialsLocal(
      Array.from(courseMaterialsLocal.filter((m) => m.id !== courseMaterial?.id))
    );
  };

  return (
    <>
      <Card className="surface grid h-full grid-rows-[min-content_auto_min-content] overflow-hidden rounded-3xl p-0">
        <div className="border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30">
          <div className="flex flex-wrap items-center justify-between gap-3 p-4">
            <h2 className="font-heading text-heading-3 font-bold text-[var(--color-mockup-text)]">
              <Translate contentKey="edocendoApp.edoCourseMaterial.home.title" />
            </h2>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <IconButton
                onClick={handleSyncList}
                disabled={loadingMaterialeInsegnamento}
                className="lg:hidden"
                aria-label="Aggiorna"
              >
                <RefreshCw className="h-4 w-4" />
              </IconButton>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSyncList}
                disabled={loadingMaterialeInsegnamento}
                className="hidden lg:inline-flex"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                <Translate contentKey="edocendoApp.edoCourseMaterial.home.refreshListLabel">
                  Refresh List
                </Translate>
              </Button>
              {isAdmin && (
                <>
                  <IconButton
                    onClick={() => handleOpenEditCourseMaterial(null)}
                    disabled={loadingMaterialeInsegnamento}
                    className="lg:hidden"
                    aria-label="Aggiungi materiale"
                  >
                    <Plus className="h-4 w-4" />
                  </IconButton>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenEditCourseMaterial(null)}
                    disabled={loadingMaterialeInsegnamento}
                    className="hidden lg:inline-flex"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Aggiungi materiale
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {loadingMaterialeInsegnamento ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div>
            {!courseMaterialsLocal.length ? (
              <div className="p-4 text-center">
                <p className="text-body text-[var(--color-mockup-text-mid)]">
                  <Translate contentKey="edocendoApp.edoCourseMaterial.home.notFound" />
                </p>
              </div>
            ) : (
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="materiale" isDropDisabled={!isAdmin}>
                  {(provided) => (
                    <div
                      className="materiale"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {courseMaterialsLocal.map((materiale, index) => (
                        <Draggable
                          key={materiale.id}
                          draggableId={String(materiale.id)}
                          index={index}
                          isDragDisabled={!isAdmin}
                        >
                          {(providedEl) => (
                            <div
                              ref={providedEl.innerRef}
                              {...providedEl.draggableProps}
                              {...providedEl.dragHandleProps}
                            >
                              <MaterialRow
                                key={materiale.id}
                                courseMaterial={materiale}
                                onOpenViewChapter={handleOpenViewChapter}
                                onOpenEditMaterial={handleOpenEditCourseMaterial}
                                onOpenDeleteMaterial={
                                  isAdmin ? handleOpenDeleteDialog : null
                                }
                                isLastElement={
                                  index === courseMaterials.length - 1
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        )}

        <Pagination
          count={materialeInsegnamentoTotalItems}
          page={paginationState.activePage - 1}
          rowsPerPage={paginationState.itemsPerPage}
          onPageChange={(page) => handlePagination(page + 1)}
          onRowsPerPageChange={handleRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} di ${count !== -1 ? count : "more than" + to}`
          }
        />
      </Card>

      <EditCourseMaterial
        fixedCourses={[{ id: parseInt(params.id) }]}
        courseMaterial={courseMaterialToEdit}
        open={editCourseMaterialDialog}
        onClose={handleCloseEditCourseMaterialDialog}
      />
      <ViewUnit
        open={viewUnitDialog}
        unit={unitToView}
        handleClose={handleCloseViewUnitDialog}
      />
      <DeleteCourseMaterial
        open={openDeleteDialog}
        courseMaterial={materialeToDelete}
        onClose={handleCloseDeleteDialog}
        onDelete={handleOnDeleteCourseMaterial}
      />
      <ViewChapter
        open={viewChapterDialog}
        chapter={chapterToView}
        onClose={handleCloseViewChapter}
      />
    </>
  );
};

export default CourseMaterials;
